import {
  createTheme,
  type MantineColorShade,
  type MantineColorsTuple,
  type MantineThemeOverride,
} from '@mantine/core';

/** VIPSo brand red */
export const VIPSO_RED = '#cc202e';
/** Soft brand wash / secondary surface */
export const VIPSO_SECONDARY = '#ffdbdd';
/** Dark navy text / surfaces */
export const VIPSO_NAVY = '#000e21';
/** App canvas — matches SCREEN_BACKGROUND */
export const VIPSO_CANVAS = '#fafafa';

const DEFAULT_BRAND: MantineColorsTuple = [
  '#ffdbdd',
  '#f8c5c9',
  '#f09aa1',
  '#e66e78',
  '#dc4452',
  '#d32d3c',
  '#cc202e',
  '#b01b27',
  '#941620',
  '#6e1018',
];

const NAVY: MantineColorsTuple = [
  '#e8eef5',
  '#c5d0e0',
  '#9aacc4',
  '#6e87a8',
  '#4a678c',
  '#2a4566',
  '#1a2f4a',
  '#0d1c33',
  '#061224',
  '#000e21',
];

const MANTINE_COLOR_KEYS = new Set([
  'dark',
  'gray',
  'red',
  'pink',
  'grape',
  'violet',
  'indigo',
  'blue',
  'cyan',
  'teal',
  'green',
  'lime',
  'yellow',
  'orange',
  'brand',
  'navy',
]);

/** Backend-persisted full theme document (JSON). */
export type TenantThemeDocument = {
  primaryColor?: string;
  secondaryColor?: string;
  logoUrl?: string | null;
  faviconUrl?: string | null;
  fontFamily?: string;
  mode?: string;
  primaryShade?: number;
  defaultRadius?: string | number;
  colors?: {
    brand?: string[];
    navy?: string[];
    [key: string]: string[] | undefined;
  };
  other?: Record<string, unknown>;
  [key: string]: unknown;
};

function clamp(n: number, min = 0, max = 255) {
  return Math.min(max, Math.max(min, Math.round(n)));
}

function parseHex(hex: string): { r: number; g: number; b: number } | null {
  const raw = hex.trim().replace(/^#/, '');
  if (!/^[0-9a-fA-F]{3}$|^[0-9a-fA-F]{6}$/.test(raw)) return null;
  const full =
    raw.length === 3
      ? raw
          .split('')
          .map((c) => c + c)
          .join('')
      : raw;
  return {
    r: parseInt(full.slice(0, 2), 16),
    g: parseInt(full.slice(2, 4), 16),
    b: parseInt(full.slice(4, 6), 16),
  };
}

function toHex(r: number, g: number, b: number) {
  return `#${[r, g, b].map((v) => clamp(v).toString(16).padStart(2, '0')).join('')}`;
}

/** Build a 10-shade Mantine tuple around a brand hex (index 6 ≈ base). */
function brandTupleFromHex(hex: string): MantineColorsTuple {
  const rgb = parseHex(hex);
  if (!rgb) return DEFAULT_BRAND;
  const { r, g, b } = rgb;
  const mix = (t: number, towardWhite: boolean) => {
    const target = towardWhite ? 255 : 0;
    return toHex(r + (target - r) * t, g + (target - g) * t, b + (target - b) * t);
  };
  return [
    mix(0.92, true),
    mix(0.8, true),
    mix(0.65, true),
    mix(0.45, true),
    mix(0.25, true),
    mix(0.1, true),
    toHex(r, g, b),
    mix(0.12, false),
    mix(0.24, false),
    mix(0.4, false),
  ];
}

function asColorTuple(colors?: string[]): MantineColorsTuple | null {
  if (!colors || colors.length < 10) return null;
  return colors.slice(0, 10) as unknown as MantineColorsTuple;
}

export const VIPSO_FONT_STACK =
  '"Montserrat", "Segoe UI", system-ui, -apple-system, sans-serif';

export function createVipsoTheme(overrides?: {
  /** Full backend theme JSON — preferred when booting from scope API */
  theme?: TenantThemeDocument | null;
  /** Hex (`#cc202e`) or a Mantine color key (`blue`, `brand`, …) */
  primaryColor?: string;
  /** Soft accent / wash hex (used as brand shade 0) */
  secondaryColor?: string;
  fontFamily?: string;
}): MantineThemeOverride {
  const doc = overrides?.theme ?? null;
  const raw = (doc?.primaryColor || overrides?.primaryColor || '').trim();
  let primaryColor = 'brand';
  let brand = asColorTuple(doc?.colors?.brand) || DEFAULT_BRAND;
  let navy = asColorTuple(doc?.colors?.navy) || NAVY;

  if (raw) {
    if (MANTINE_COLOR_KEYS.has(raw)) {
      primaryColor = raw;
    } else if (parseHex(raw) && !doc?.colors?.brand) {
      brand = brandTupleFromHex(raw);
      primaryColor = 'brand';
    } else if (parseHex(raw)) {
      primaryColor = 'brand';
    }
  }

  const secondary = (doc?.secondaryColor || overrides?.secondaryColor || '').trim();
  if (secondary && parseHex(secondary)) {
    const shades = [...brand];
    shades[0] = secondary;
    brand = shades as unknown as MantineColorsTuple;
  }

  const fontFamily = doc?.fontFamily || overrides?.fontFamily || VIPSO_FONT_STACK;
  const primaryShade = (
    typeof doc?.primaryShade === 'number' ? doc.primaryShade : 6
  ) as MantineColorShade;
  const defaultRadius = (doc?.defaultRadius as MantineThemeOverride['defaultRadius']) || 'md';

  return createTheme({
    primaryColor,
    colors: { brand, navy },
    fontFamily,
    headings: { fontFamily, fontWeight: '700' },
    defaultRadius,
    primaryShade,
    other: {
      vipsoNavy: VIPSO_NAVY,
      vipsoCanvas: VIPSO_CANVAS,
      vipsoRed: VIPSO_RED,
      vipsoSecondary: secondary || VIPSO_SECONDARY,
      logoUrl: doc?.logoUrl ?? null,
      ...(doc?.other || {}),
    },
  });
}

export { VipsoProvider, type VipsoProviderProps } from './VipsoProvider';
